"use client";

import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from "react";
import { GridRowId } from "@mui/x-data-grid-pro";
import { calculateInitialEmailCadence } from "../helpers";
import { FetchCampaignsV2Response } from "@/api/interfaces/responses/campaigns";
import { EmailCadence } from "@/interfaces/campaign";

interface NotifyDialogContextType {
  campaignIdsToNotify: GridRowId[];
  closeNotificationsDialog: () => void;
  emailCadence: EmailCadence;
  notificationsDialogOpen: boolean;
  openNotificationsDialog: (campaignIds: GridRowId[]) => void;
  setEmailCadence: (value: EmailCadence) => void;
}

const NotifyDialogContext = createContext<NotifyDialogContextType | undefined>(
  undefined,
);

export const useNotifyDialog = () => {
  const context = useContext(NotifyDialogContext);
  if (!context) {
    throw new Error("useNotifyDialog must be used within NotifyDialogProvider");
  }
  return context;
};

interface NotifyDialogProviderProps {
  children: ReactNode;
  data: FetchCampaignsV2Response[];
}

export function NotifyDialogProvider({
  children,
  data,
}: NotifyDialogProviderProps) {
  const [notificationsDialogOpen, setNotificationsDialogOpen] = useState(false);
  const [campaignIdsToNotify, setCampaignIdsToNotify] = useState<GridRowId[]>(
    [],
  );
  const [emailCadence, setEmailCadence] = useState<EmailCadence>(
    EmailCadence.Unsubscribed,
  );

  const openNotificationsDialog = useCallback(
    (campaignIds: GridRowId[]) => {
      setCampaignIdsToNotify(campaignIds);
      const initialEmailCadence = calculateInitialEmailCadence(
        data.filter((campaign) => campaignIds.includes(campaign.id)),
      );
      setEmailCadence(initialEmailCadence);
      setNotificationsDialogOpen(true);
    },
    [data],
  );

  const closeNotificationsDialog = useCallback(() => {
    setNotificationsDialogOpen(false);
  }, []);

  return (
    <NotifyDialogContext.Provider
      value={{
        campaignIdsToNotify,
        closeNotificationsDialog,
        emailCadence,
        openNotificationsDialog,
        notificationsDialogOpen,
        setEmailCadence,
      }}
    >
      {children}
    </NotifyDialogContext.Provider>
  );
}
